import { Typography } from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { dynamicSort } from '@commonClient2/services/sort';
import { BidProcessType, ScheduleSource } from '../../../../Enums';
import {
    getScheduleSourceLabel,
    getAutoApprovalLabel,
    getQualificationRuleLabel,
    getBidProcessTypeLabel,
    getDayOfTheWeekLabel,
    getEnabledDisabledLabel,
} from '../../../../Enums/Helpers/LabellingHelper';
import './SetupPreview.scss';

/**
 * @param {{
 *  label: string
 *  value: string
 * }} props
 */
const RowInfo = ({ label, value }) => (
    <Typography variant="subtitle2">
        {label}
        <span className="sup-bolded-value-label">
            {value}
        </span>
    </Typography>
);

/**
 * @param {{bidSetupId: number}} props
 */
function SetupPreview(props) {
    const { bidSetupId } = props;
    const { t } = useTranslation('shiftBidding', { keyPrefix: 'manager.bidProfileEdit.setupPreview' });
    /** @type {ShiftBidding.ManagerStore} */
    // @ts-ignore
    const { managerAppData } = useSelector((state) => state);
    const { managerBidSetups } = managerAppData;

    const selectedSetup = managerBidSetups.find((x) => x.bidSetupId === bidSetupId);
    if (!selectedSetup) {
        return <></>;
    }

    const {
        bidProcessType, isAutoApproval, isEmployeeCanDeclineBid, isEmployeeMustSubmitRequiredAmountOfBids, qualificationRule,
        scheduleSource, isRebidAllowed, isAllowEmployeesToLockForRebid, isBidOnBehalfAllowedWhenActive, isBidOnBehalfAllowedWhenClosed,
        isEditBidOnBehalfAllowedWhenActive, isEditBidOnBehalfAllowedWhenClosed, anchorDay, isOverrideSchedule,
        isAutoSignOff, awardingRuleSetName, isEnableEmployeeRankReport, isEnableEmployeeBidResultReport, isEditEmployeesWhenBidActive,
        isAllowBidOnActiveWorkplan, isRecalculateEmployeeRanksAllowedWhenActiveOrClosed,
    } = selectedSetup;

    const moreInfo = [];

    let firstDayOfTheWeek = getDayOfTheWeekLabel(anchorDay);
    if (scheduleSource === ScheduleSource.WorkplanManager) {
        firstDayOfTheWeek = t('impotedFromWorkplan');
        moreInfo.push({
            label: t('isAllowBidOnActiveWorkplan'),
            value: getEnabledDisabledLabel(isAllowBidOnActiveWorkplan),
        });
    } else { // ScheduleSource.ExcelFile
        moreInfo.push({
            label: t('isOverrideSchedule'),
            value: getEnabledDisabledLabel(isOverrideSchedule),
        });
    }

    const setupInfo = [
        {
            label: t('bidProcessType'),
            value: getBidProcessTypeLabel(bidProcessType),
        },
        {
            label: t('isAutoApproval'),
            value: getAutoApprovalLabel(isAutoApproval),
        },
        {
            label: t('isEmployeeCanDeclineBid'),
            value: getEnabledDisabledLabel(isEmployeeCanDeclineBid),
        },
        {
            label: t('isEmployeeMustSubmitRequiredAmountOfBids'),
            value: getEnabledDisabledLabel(isEmployeeMustSubmitRequiredAmountOfBids),
        },
        {
            label: t('qualificationRule'),
            value: getQualificationRuleLabel(qualificationRule),
        },
        {
            label: t('scheduleSource'),
            value: getScheduleSourceLabel(scheduleSource),
        },
        {
            label: t('firstDayOfTheWeek'),
            value: firstDayOfTheWeek,
        },
        {
            label: t('isAutoSignOff'),
            value: getEnabledDisabledLabel(isAutoSignOff),
        },
        {
            label: t('awardingRuleSetName'),
            value: awardingRuleSetName,
        },
        {
            label: t('isEnableEmployeeRankReport'),
            value: getEnabledDisabledLabel(isEnableEmployeeRankReport),
        },
        {
            label: t('isEnableEmployeeBidResultReport'),
            value: getEnabledDisabledLabel(isEnableEmployeeBidResultReport),
        },
        {
            label: t('isBidOnBehalfAllowedWhenActive'),
            value: getEnabledDisabledLabel(isBidOnBehalfAllowedWhenActive),
        },
        {
            label: t('isBidOnBehalfAllowedWhenClosed'),
            value: getEnabledDisabledLabel(isBidOnBehalfAllowedWhenClosed),
        },
        {
            label: t('isEditBidOnBehalfAllowedWhenActive'),
            value: getEnabledDisabledLabel(isEditBidOnBehalfAllowedWhenActive),
        },
        {
            label: t('isEditBidOnBehalfAllowedWhenClosed'),
            value: getEnabledDisabledLabel(isEditBidOnBehalfAllowedWhenClosed),
        },
        {
            label: t('isEditEmployeesWhenBidActive'),
            value: getEnabledDisabledLabel(isEditEmployeesWhenBidActive),
        },
    ];

    if (bidProcessType === BidProcessType.Preferential) {
        moreInfo.push({
            label: t('isRebidAllowed'),
            value: getEnabledDisabledLabel(isRebidAllowed),
        });
        moreInfo.push({
            label: t('isRecalculateEmployeeRanksAllowedWhenActiveOrClosed'),
            value: getEnabledDisabledLabel(isRecalculateEmployeeRanksAllowedWhenActiveOrClosed),
        });
        if (isRebidAllowed) {
            moreInfo.push({
                label: t('isAllowEmployeesToLockForRebid'),
                value: getEnabledDisabledLabel(isAllowEmployeesToLockForRebid),
            });
        }
    }

    const allInfo = setupInfo.concat(moreInfo);
    allInfo.sort(dynamicSort('label'));

    return (
        <Alert severity="info">
            <AlertTitle>{t('title')}</AlertTitle>
            {allInfo.map((row) => (
                <RowInfo
                    key={`${row.label}_${row.value}`}
                    label={row.label}
                    value={row.value}
                />
            ))}
        </Alert>
    );
}

export default SetupPreview;
